import axios from 'axios';
import { useState, useEffect } from 'react';
import { formatDate } from 'src/utils/dateFns/formatDate';
import { addDays } from '@fluentui/react';
import { TimeTracking } from '../models/TimesheetsInterface';
import { useTimesheetsContext } from 'src/context/TimeSheet/TimesheetsContext';
import moment from 'moment';

export const useGetTimesheetsData = (assignmentsDateRange: Date) => {
  const [timesheetsData, setTimesheetsData] = useState<TimeTracking[]>([]);
  const [loadingTimesheets, setLoadingTimesheets] = useState(false);

  const { day, month, year } = formatDate(assignmentsDateRange);
  const {
    day: nextWeekDay,
    month: nextWeekMonth,
    year: nextWeekYear,
  } = formatDate(addDays(assignmentsDateRange, 6));

  const { setIsReadPeriod, showInactivePeriods } = useTimesheetsContext();

  const dayEntriesDates = {
    startDate: `${year}-${month}-${day}`,
    endDate: `${nextWeekYear}-${nextWeekMonth}-${nextWeekDay}`,
  };

  const getAssignments = async () => {
    try {
      setLoadingTimesheets(true);
      let { data } = await axios.get(
        `api/timetracking?startDate=${dayEntriesDates.startDate}&endDate=${
          dayEntriesDates.endDate
        }&showPastPeriods=${showInactivePeriods || ''}`
      );

      const sortedAssignments = data.sort((assignment1, assignment2) => {
        if (
          assignment1.assignment.new_assignmenttype >
          assignment2.assignment.new_assignmenttype
        )
          return 1;
        if (
          assignment1.assignment.new_assignmenttype <
          assignment2.assignment.new_assignmenttype
        )
          return -1;
        return assignment1.assignment.new_title.localeCompare(
          assignment2.assignment.new_title
        );
      });

      const assignmentNames = data.map((data) => data.assignment.new_title);
      const duplicateAssignmentNames = assignmentNames.filter(
        (data, index) => assignmentNames.indexOf(data) !== index
      );
      data = data.reduce((acc, curr) => {
        const assignmentName = curr.assignment.new_title;
        if (duplicateAssignmentNames.includes(assignmentName)) {
          curr.assignment.new_title += ` (${moment(curr.assignment.new_enddate).year()})`;
        }
        return [...acc, curr];
      }, []);

      setTimesheetsData(sortedAssignments);
    } catch (error) {
      console.error('Error fetching timesheets data:', error);
    } finally {
      setLoadingTimesheets(false);
    }
  };

  const projectEntries = timesheetsData.filter(
    (entry) =>
      entry?.assignment?.new_assignmenttype === 100000000 ||
      entry?.assignment?.new_assignmenttype === 100000001
  );

  const isReadPeriod = projectEntries.every((data) =>
    data.dayEntries
      .filter((entry) => !!entry.statecode || entry.statecode === 0)
      .filter((entry) => entry.new_daytype === 100000000)
      .every((entry) => entry.statecode !== 0)
  );

  setIsReadPeriod(!loadingTimesheets && projectEntries && isReadPeriod);

  useEffect(() => {
    getAssignments();
  }, [assignmentsDateRange, showInactivePeriods]);

  return {
    timesheetsData,
    getAssignments,
    loadingTimesheets,
  };
};
